import pako from 'pako';

export class CompressionUtil {
    private static readonly PREFIX = 'CMP:';
    private static readonly MAX_SIZE = 1024 * 1024; // 1MB max

    private static safeStringify(obj: any): string | null {
        if (!obj || typeof obj !== 'object') return null;
        
        const seen = new WeakSet();
        try {
            const json = JSON.stringify(obj, (key, value) => {
                if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) return '[Circular]';
                    seen.add(value);
                }
                return value === undefined ? null : value;
            });
            return json;
        } catch (e) {
            console.warn('[CompressionUtil] Falha ao stringify:', e);
            return null;
        }
    }

    static compress(data: any): string | null {
        // Tratar dados inválidos: null, undefined, primitivos
        if (!data || typeof data !== 'object') {
            return null;
        }

        try {
            // Se for array vazio ou objeto vazio, retornar null
            if (Array.isArray(data) && data.length === 0) {
                return null;
            }
            if (!Array.isArray(data) && Object.keys(data).length === 0) {
                return null;
            }

            const json = this.safeStringify(data);
            if (!json) return null;
            
            // Verificar tamanho máximo
            if (json.length > this.MAX_SIZE) {
                console.warn('[CompressionUtil] Dados muito grandes:', json.length);
                return null;
            }
            
            // Se stringify resultar em objeto vazio, retornar null
            if (json === '{}' || json === '[]') {
                return null;
            }

            const compressed = pako.deflate(json);
            const base64 = btoa(String.fromCharCode(...compressed));
            return this.PREFIX + base64;
        } catch (e) {
            console.warn('[CompressionUtil] Falha ao comprimir dados:', e);
            return null;
        }
    }

    static decompress(encoded: string): any {
        if (!encoded) return null;

        try {
            // Verificar se é dados compactados (começa com prefixo)
            if (encoded.startsWith(this.PREFIX)) {
                const base64 = encoded.substring(this.PREFIX.length);
                // Verificar tamanho antes de processar
                if (base64.length > this.MAX_SIZE) {
                    console.warn('[CompressionUtil] Dados compactados muito grandes:', base64.length);
                    return encoded;
                }
                const binary = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
                const decompressed = pako.inflate(binary, { to: 'string' });
                return JSON.parse(decompressed);
            }

            // Se não tem prefixo, pode ser JSON normal (dados antigos)
            if (encoded.trim().startsWith('{') || encoded.trim().startsWith('[')) {
                return JSON.parse(encoded);
            }

            // Se não é nenhum dos dois, retornar como string
            return encoded;
        } catch (e) {
            console.warn('[CompressionUtil] Falha ao descomprimir:', e);
            // Retornar o valor original para não quebrar a UI
            return encoded;
        }
    }

    static isCompressed(data: string): boolean {
        return !!data && data.startsWith(this.PREFIX);
    }
}
